import { Injectable } from '@angular/core';
import { BiometricsAPI } from '@app/data/mo-api/biometrics.api';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  catchError,
  map,
  concatMap,
  switchMap,
  takeWhile,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { interval, of } from 'rxjs';
import { EBIOMETRICS_STATUS, EBIOMETRICS_STEPS } from '@app/core/enums/biometrics.enum';
import { ERROR_COMPONENT_CONSTANTS as ERROR } from '@app/domain/services/error/error.constants';
import { EERROR_PAGE_TYPES } from '@app/core/models/error.model';
import { APP_CONSTANTS as CONST } from '@app/app.constants';

import * as biometricsActions from '@store/biometrics/biometrics.actions';
import * as errorActions from '@store/error/error.actions';
import * as appSelectors from '@store/app/app.selectors';
import { AppFacade } from '@app/facades/app.facade';
import { BiometricsFacade } from '@app/facades/biometrics.facade';
import { ErrorFacade } from '@app/facades/error.facade';
import { APP_ROUTES } from '@app/app.routes';
import { UtilService } from '@app/services/util.service';
import { StorageService } from '@app/data/browser/storage.service';

@Injectable()
export class BiometricsEffects {
  constructor(
    private actions$: Actions,
    private _store: Store,
    private _biometricsFacade: BiometricsFacade,
    private _appFacade: AppFacade,
    private _biometricsAPI: BiometricsAPI,
    private _utilService: UtilService,
    private _storageService: StorageService
  ) {}

  public getKeynuaWidgetToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(biometricsActions.getKeynuaWidgetToken),
      withLatestFrom(this._store.select(appSelectors.selectTransactions)),
      switchMap(([_, { id }]) => {
        return this._biometricsAPI.getKeynuaWidgetResponse(id).pipe(
          map((response) => {
            switch (response?.status) {
              case EBIOMETRICS_STATUS.PENDING:
                if (!!response?.token)
                  return biometricsActions.successGetKeynuaWidgetToken({
                    keynuaWidgetResponse: response,
                  });
                break;
              case EBIOMETRICS_STATUS.FINISHED:
                const route: string = APP_ROUTES.CALCULATE;
                this._biometricsFacade.setIsBiometricsFlag(true);
                this._utilService.navigateTo(route);
                return biometricsActions.successGetKeynuaWidgetToken({
                  keynuaWidgetResponse: response,
                });

              case EBIOMETRICS_STATUS.RETRY:
                const errorRetry = ERROR[EERROR_PAGE_TYPES.ERROR_26];
                return errorActions.showErrorComponent({ errorComponentData: errorRetry });
            }
          }),
          catchError((error) => of(biometricsActions.failureGetKeynuaWidgetToken({ error })))
        );
      })
    )
  );

  public getKeynuaWidgetStatus$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(biometricsActions.getKeynuaWidgetStatus),
        withLatestFrom(this._store.select(appSelectors.selectTransactions)),
        switchMap(([_, { id }]) =>
          interval(CONST.BIOMETRICS_REQUEST_LOOP).pipe(
            concatMap(() => {
              return this._biometricsAPI.getKeynuaWidgetResponse(id);
            }),
            tap((response) => {
              this._biometricsFacade.successGetKeynuaWidgetStatus(response);
            }),
            takeWhile(
              (response) => !response?.token && response?.status === EBIOMETRICS_STATUS.PENDING,
              true
            ),
            catchError((error) => of(biometricsActions.failureGetKeynuaWidgetStatus({ error })))
          )
        )
      ),
    { dispatch: false }
  );

  public successGetKeynuaWidgetStatus$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(biometricsActions.successGetKeynuaWidgetStatus),
        tap(({ keynuaWidgetResponse }) => {
          const { status } = keynuaWidgetResponse;
          let route: string;
          switch (status) {
            case EBIOMETRICS_STATUS.RETRY:
              route = APP_ROUTES.BIOMETRICS;
              this._biometricsFacade.resetKeynuaWidgetResponse();
              this._biometricsFacade.setBiometricsStep(EBIOMETRICS_STEPS.CAMERA);
              return this._utilService.navigateTo(route);

            case EBIOMETRICS_STATUS.FINISHED:
              route = APP_ROUTES.VALIDATIONS;
              const previousRoute: string = APP_ROUTES.BIOMETRICS;
              this._biometricsFacade.setBiometricsStep(EBIOMETRICS_STEPS.CAMERA);
              this._biometricsFacade.setIsBiometricsFlag(true);
              this._appFacade.setPreviousRoute(previousRoute);
              this._storageService.setItem('previousRoute', previousRoute);
              return this._utilService.navigateTo(route);
          }
        })
      ),
    { dispatch: false }
  );
}
